import { mergeProps, Show, splitProps, type ComponentProps } from "solid-js";
import type { ClassValue } from "~/util/html/style";
import clsx from "~/util/html/style";

type ToggleProps = Omit<ComponentProps<"input">, "size" | "type"> & {
  label?: string;
  size?: "small" | "medium";
  class?: ClassValue;
};

export function Toggle(props: ToggleProps) {
  const mergedProps = mergeProps({ size: "medium" }, props);
  const [local, rest] = splitProps(mergedProps, ["label", "size", "class", "checked", "disabled"]);

  // Use the prop value directly for visual state, not the native checkbox :checked state
  // This ensures the toggle reflects server state even when the server rejects a change
  const isChecked = () => local.checked ?? false;

  return (
    <div class={clsx(local.class)}>
      <label
        class={clsx(
          "group relative inline-flex cursor-pointer items-center justify-between",
          local.disabled && "cursor-not-allowed opacity-50",
          "m-0", // needed because there are global label styles
        )}
      >
        <Show when={local.label}>
          <span class="mr-3 text-sm font-medium text-typography-primary">{local.label}</span>
        </Show>

        <input
          {...rest}
          type="checkbox"
          class="peer sr-only"
          checked={local.checked}
          disabled={local.disabled}
          value={rest.value ?? "1"}
        />

        <div
          class={clsx(
            "relative rounded-full transition-colors duration-200 ease-in-out",
            "peer-focus:ring-2 peer-focus:ring-brand-primary peer-focus:ring-offset-2",
            isChecked() ? "bg-brand-primary" : "bg-gray-200",
            local.size === "small" && "h-4 w-8",
            local.size === "medium" && "h-6 w-11",
          )}
        >
          <div
            class={clsx(
              "absolute transition-all duration-200 ease-in-out",
              isChecked() ? "left-full -translate-x-full" : "left-0",
              local.size === "small" && "h-4 w-4 p-0.5",
              local.size === "medium" && "h-6 w-6 p-0.5",
            )}
          >
            <div class="h-full w-full rounded-full bg-white shadow-sm" />
          </div>
        </div>
      </label>
    </div>
  );
}
